import {
  Component,
  input,
  OnDestroy,
  OnInit,
  output,
  Renderer2,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ServiceInfosResolver } from 'src/app/resolvers';
import { IServicesFilters, deserializeServicesFilters, serializeServicesPanelFilters } from 'src/app/filters';
import { LiteralService, StorageService, UtilsService } from 'src/app/services';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { initialServiceInfoDetailsState } from 'src/app/store/initialisation/service-infos';
import { images } from 'src/images';

@Component({
  selector: 'app-service-modal',
  templateUrl: './service-modal.component.html',
  styleUrl: './service-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ServiceModalComponent implements OnInit, OnDestroy {
  private queryParamsSub?: Subscription;
  serviceInfos = signal(initialServiceInfoDetailsState);
  readonly panelFilters = signal<IServicesFilters | undefined>(undefined);
  readonly serviceInfoSelected = input<any>();
  selectServiceInfo = output<any>();
  openGTFSModal = output();
  openExportGTFSModal = output();
  openImportExcelModal = output();
  showRoutes = output<any>();
  addService = output();

  public searching = false;
  public showGTFsOptions = false;
  public searchValue: string;

  public images = images;
  public TooltipPosition = TooltipPosition;

  constructor(
    renderer: Renderer2,
    public literalService: LiteralService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private serviceInfosResolver: ServiceInfosResolver,
    private utilsService: UtilsService,
    private storageService: StorageService,
  ) {
    renderer.listen('window', 'click', (e: Event) => {
      const modal = document.getElementById('gtfsOptions')!;
      const componentOpen = document.getElementById('gtfsImportIcon')!;
      const close = utilsService.closeCustomModals(e, modal, componentOpen);
      if (close) {
        this.showGTFsOptions = false;
      }
    });
  }

  ngOnInit() {
    this.applyQueryParamsToState(
      this.activatedRoute.snapshot.queryParamMap.get('page'),
      this.activatedRoute.snapshot.queryParamMap.get('search'),
    );
    this.panelFilters.set(deserializeServicesFilters(this.activatedRoute.snapshot.queryParamMap));
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe(
      (params) => {
        this.applyQueryParamsToState(params.get('page'), params.get('search'));
        this.panelFilters.set(deserializeServicesFilters(params));
      },
    );
  }

  onFilter(filters: IServicesFilters) {
    const panelFilterParams = serializeServicesPanelFilters(filters);
    this.storageService.setServicesFilters(JSON.stringify(panelFilterParams));
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { ...panelFilterParams, page: null },
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
  }

  private applyQueryParamsToState(
    pageRaw: string | null,
    searchRaw: string | null,
  ): void {
    if (pageRaw) {
      const page = Number(pageRaw);
      if (!isNaN(page) && page > 0)
        this.serviceInfos().pagination.actualPage = page;
    } else {
      this.serviceInfos().pagination.actualPage = 1;
    }
    this.searchValue = searchRaw ?? '';
  }

  showGTFs(event: any) {
    event.stopPropagation();
    this.showGTFsOptions = !this.showGTFsOptions;
    const gtfsIcon = document
      .getElementById('gtfsImportIcon')
      ?.getBoundingClientRect();
    if (gtfsIcon) {
      document.getElementById('gtfsOptions')!.style.top =
        (gtfsIcon.top - 70).toString() + 'px';
      document.getElementById('gtfsOptions')!.style.left =
        (gtfsIcon.left - 120).toString() + 'px';
    }
  }

  changePageService(event: { isNext: boolean; specificPage?: number }) {
    const currentPage = this.serviceInfos().pagination.actualPage || 1;
    const newPage =
      event.specificPage ?? (event.isNext ? currentPage + 1 : currentPage - 1);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { page: newPage > 1 ? newPage : null },
      queryParamsHandling: 'merge',
    });
  }

  async getServiceInfos() {
    const result = this.searchValue
      ? await this.serviceInfosResolver.getServiceInfos(
          this.activatedRoute.snapshot,
          this.searchValue,
        )
      : await this.serviceInfosResolver.resolveWithPage(
          this.serviceInfos().pagination.actualPage || 1,
          this.activatedRoute.snapshot,
        );
    if (!result) {
      this.serviceInfos.update((state) => ({
        ...state,
        isFetchingServiceInfos: false,
      }));
      return;
    }
    this.serviceInfos.set({
      ...result,
      isFetchingServiceInfos: false,
      value: [...result.value],
      pagination: { ...result.pagination },
    });
  }

  public search(event: any) {
    this.searchValue = event || '';
    this.serviceInfos.update((s) => ({
      ...s,
      isFetchingServiceInfos: true,
      value: [],
    }));
    this.getServiceInfos();
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { search: this.searchValue || null, page: null },
      queryParamsHandling: 'merge',
    });
  }
}
